import { ReactNode } from "react"
import { DndProvider } from "react-dnd"
import { TouchBackend } from "react-dnd-touch-backend"
import { MultiBackend, createTransition } from "dnd-multi-backend"
import { HTML5Backend } from "react-dnd-html5-backend"

type DragAndDropProviderProps = {
    children: ReactNode
}

/**
 * We need a multibackend because of the new layers panel. When dragging layers,
 * we don't want an image preview. We previously were doing this by just making
 * the preview image a fully transparent png. But since we can reject drag and
 * drops now (because of folder depth issues), the browser 'snap-back' animation
 * causes the dropzone to linger for up to a couple seconds after an unsuccessful
 * drop. By switching to the touch backend, the browser doesn't play the snap-back
 * animation, and we get no preview image without having to put an empty png. We
 * do, however, want the preview image for other drag and drop areas, such as
 * dragging folders in a workspace. For that, we still need the HTML5 backend.
 * The multibackend allows us to switch between the two based on a data-drag-type
 * attribute on the element being dragged.
 *
 * TLDR: Add a data-drag-type="touch" if you want no preview image and no snap-back
 * animation, otherwise don't.
 * - Connor
 */
const hasTouchDragType = (target: EventTarget | null): boolean => {
    let el = target as HTMLElement | null
    while (el) {
        if (el.dataset?.dragType === "touch") return true
        el = el.parentElement
    }
    return false
}

const TouchTransition = createTransition("mousedown", (event) => hasTouchDragType(event.target))
const HTML5Transition = createTransition("mousedown", (event) => !hasTouchDragType(event.target))
const backends = [
    {
        id: "touch",
        backend: TouchBackend,
        transition: TouchTransition,
        options: { enableMouseEvents: true },
    },
    {
        id: "html5",
        backend: HTML5Backend,
        transition: HTML5Transition,
    },
]

/**
 * an integration wrapper to setup the react-dnd module
 */
export default function DragAndDropProvider({ children }: DragAndDropProviderProps) {
    return (
        <DndProvider backend={MultiBackend} options={{ backends }}>
            {children}
        </DndProvider>
    )
}
